import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import HostedCardPanel from './payments/HostedCardPanel';
import './paymentResultOverlay.css';
import {
    isPaymentInReview,
    paymentInReviewMessage,
    isPendingDrift,
    driftCaptureTotal,
    pendingDriftMessage,
    paymentErrorMessage,
    readCancelSettlement,
    readCancelErrorSettlement,
    CANCEL_UNCONFIRMED_MESSAGE,
} from './paymentErrors';

// Full-screen payment-result experience for the hosted-paypage return leg.
// Rendered by the dedicated return pages (/client/payment-return/<surface>)
// and by any client surface that can still receive a legacy
// ?payment=return&ref=… redirect, fed by usePaymentReturn:
//
//   <PaymentResultOverlay payReturn={payReturn} surface="wallet" />
//
// States (payReturn.status):
//   checking        → brand spinner while /verify polls the gateway
//   paid            → big animated check, amount + purpose; PERSISTS until the
//                     client clicks the explicit CTA (the owner: payment outcomes
//                     NEVER auto-dismiss — no timers, no toasts)
//   verified        → card-verification success (AUTH+void rail): card saved,
//                     nothing captured, no wallet row. Same persistent green
//                     check, but no amount/purpose figures and the body is the
//                     backend's verification.message hold line — never framed
//                     as a payment
//   failed          → red; nothing was charged; "Try payment again" (fresh
//                     checkout via POST /api/payments/retry) and "Cancel this
//                     payment" (POST /api/payments/cancel → confirmation state)
//   expired         → amber; the paypage timed out; same retry/cancel rails
//   still_pending   → amber; polling window elapsed; backend poller will settle
//   not_found       → neutral; the ref doesn't belong to this account — terminal
//   unauthenticated → neutral; no session on this origin; "Log in" preserving
//                     the return URL (payReturn.loginUrl)
//
// After a successful cancel the overlay shows its own persistent "Payment
// cancelled" confirmation; a host page holding a saved booking draft can pass
// onEditResubmit to offer "Edit & resubmit" (reopens the request modal
// prefilled from the persisted draft — the client re-types nothing).
// Offered on ANY surface: a cancelled mid-booking top-up legitimately returns
// to the wallet surface (item 4), where the draft is just as stranded.
//
// The overlay never traps: every terminal state has a working CTA, a corner
// close, Escape and scrim-click dismissal. 'checking' is the only state
// without dismissal — it always resolves within the hook's 30s window.

const money = (n) =>
    `AED ${Number(n).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const SURFACE = {
    wallet: {
        cta: 'Back to wallet',
        purposeFallback: 'Wallet top-up',
        paidBody: 'Your payment was received and your wallet has been credited.',
        failedBody: 'No money was taken and nothing was credited to your wallet.',
        expiredBody: 'The secure payment page timed out before the payment was completed. No money was taken.',
        pendingBody: 'Your payment is still being confirmed — we’ll update your account automatically once the bank responds.',
        cancelledBody: 'This payment has been cancelled — nothing was charged. Your wallet is unchanged.',
    },
    invoices: {
        cta: 'View invoices',
        purposeFallback: 'Invoice payment',
        paidBody: 'Your payment was received and has been applied to your outstanding invoices.',
        failedBody: 'No money was taken and your invoices remain unpaid.',
        expiredBody: 'The secure payment page timed out before the payment was completed. No money was taken.',
        pendingBody: 'Your payment is still being confirmed — we’ll update your account automatically once the bank responds.',
        cancelledBody: 'This payment has been cancelled — nothing was charged. Your invoices remain unpaid.',
    },
    bookings: {
        cta: 'View my booking request',
        purposeFallback: 'Booking payment',
        paidBody: 'Your payment was received and your booking request has been submitted for review. We’ll notify you the moment our team confirms it.',
        failedBody: 'Your booking request has not been submitted yet and no money was taken. Your details are saved — trying again picks up right where you left off.',
        expiredBody: 'The secure payment page timed out — your booking request has not been submitted yet. No money was taken. Your details are saved, so trying again needs no re-typing.',
        pendingBody: 'Your payment is still being confirmed — we’ll update your account automatically once the bank responds. Your booking request will appear once it is confirmed.',
        cancelledBody: 'This payment has been cancelled — nothing was charged and the booking request was not submitted.',
    },
    account: {
        // CARD SETUP (add-card / verification) — this surface is NOT a payment
        // result. The rail places a temporary 1 AED authorisation hold and the
        // backend releases (voids) it automatically: nothing is captured and no
        // wallet row exists. Every title/body here speaks of the card and the
        // hold — never "payment successful", never a top-up, never the wallet.
        //
        // TWO success outcomes reach this surface (MONEY CONTRACT — they must
        // not share copy because different things happened to the money):
        //   'verified' → the AUTH+void rail. Rendered from the verify
        //     payload's own verification.message, which says "you have not
        //     been charged" ONLY when the backend verifiably released the
        //     hold, and otherwise describes a standing hold that lapses.
        //   'paid'     → a LEGACY pre-switch SALE checkout (only possible for
        //     checkouts staked before the AUTH rail shipped — they hard-expire
        //     at 24h). That cohort genuinely CAPTURED 1 AED and credited it to
        //     the wallet, so paidBody says exactly that — a hold/released
        //     claim would be untrue for them.
        cta: 'Back to account',
        purposeFallback: 'Card verification',
        checkingTitle: 'Confirming your card…',
        paidTitle: 'Card saved',
        verifiedTitle: 'Card saved',
        failedTitle: 'Card was not saved',
        expiredTitle: 'Card setup session expired',
        pendingTitle: 'Still confirming your card',
        cancelledTitle: 'Card setup cancelled',
        retryLabel: 'Try again',
        cancelLabel: 'Cancel card setup',
        paidBody: 'Your card has been verified and saved securely. The AED 1.00 verification payment was received and has been credited to your wallet.',
        failedBody: 'Your card was not saved and no money was taken.',
        expiredBody: 'The secure page timed out before your card could be verified. Your card was not saved and no money was taken.',
        pendingBody: 'Your card verification is still being confirmed — we’ll update your account automatically once the bank responds. Any temporary hold is released automatically.',
        cancelledBody: 'The card setup has been cancelled — your card was not saved and nothing was charged.',
    },
};

// Body for the 'verified' success state (the card-verification AUTH+void
// rail). The backend's verification.message is the SINGLE truthful line about
// the hold — "you have not been charged" only rides on a verifiably released
// void, otherwise it describes a standing hold that lapses on its own
// (routes/payments.js verificationClientMessage). Never paraphrase it here.
// The fallback (payload missing the block — old server mid-deploy) says only
// what we know: the card was saved. It makes NO claim about the hold either
// way.
const VERIFIED_FALLBACK_BODY = 'Your card has been verified and saved securely.';
const verifiedStateBody = (data, surface) => {
    const msg = data?.verification?.message || VERIFIED_FALLBACK_BODY;
    // Auto-pay first-card sign-up: the verification also FILED the staged
    // booking request — say so, and say when the real charge happens.
    if (surface === 'bookings' && isAutopaySetupKind(data)) {
        return `${msg} Your booking is requested — the booking amount will be charged to this card only after the cleaning is completed.`;
    }
    return msg;
};

// Paid/failed copy branch for booking CHANGE checkouts (edit-request-checkout
// returns to the bookings surface too — the client changed an existing
// booking, they did not request a new one).
// AUTO-PAY FIRST-CARD SIGN-UP returning to the bookings surface. Nothing was
// captured at all — the rail places a temporary AED 1.00 verification hold
// that the backend releases automatically, NOT a booking payment — so the
// generic bookings copy ("Your payment was received and your booking request
// has been submitted") would let the client read the booking as paid. It
// isn't: it is charged after the cleaning is completed. Detected off the
// checkout kind the server stamps (`autopay_setup`), with the purpose/label as
// a fallback.
const isAutopaySetupKind = (result) => {
    const k = `${result?.kind || ''} ${result?.purpose || ''} ${result?.kind_label || ''}`.toLowerCase();
    return k.includes('autopay_setup') || k.includes('autopay setup');
};

const isEditKind = (result) => {
    const k = `${result?.kind || ''} ${result?.purpose || ''} ${result?.kind_label || ''}`.toLowerCase();
    return k.includes('edit') || k.includes('change');
};

// A checkout whose money went to the WALLET (or rode through it to invoices /
// price adjustments) — it never files a booking request. Such checkouts CAN
// return to the bookings surface (the adjustment card rail returns there by
// design), where the generic bookings paidBody ("your booking request has been
// submitted for review") would be a false claim about a request that was never
// made. The kind is the server's own stamp on the checkout row.
const isTopupKind = (result) => {
    const k = `${result?.kind || ''}`.toLowerCase();
    return k === 'topup' || k === 'settle_pending';
};

// The 'topup' checkout that is actually funding PRICE ADJUSTMENTS — the server
// labels it so (routes/payments.js checkoutFundsAdjustments) and the sweep
// settles the rows on capture.
const isAdjustmentFunding = (result) =>
    /price adjustment/i.test(`${result?.kind_label || ''} ${result?.purpose || ''}`);

const CheckIcon = () => (
    <svg viewBox="0 0 52 52" aria-hidden="true">
        <circle className="payres__iconRing" cx="26" cy="26" r="24" fill="none" strokeWidth="3" />
        <path className="payres__iconDraw" fill="none" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round" d="M14 27l8 8 16-17" />
    </svg>
);

const CrossIcon = () => (
    <svg viewBox="0 0 52 52" aria-hidden="true">
        <circle className="payres__iconRing" cx="26" cy="26" r="24" fill="none" strokeWidth="3" />
        <path className="payres__iconDraw" fill="none" strokeWidth="4" strokeLinecap="round" d="M18 18l16 16M34 18L18 34" />
    </svg>
);

const ClockIcon = () => (
    <svg viewBox="0 0 52 52" aria-hidden="true">
        <circle className="payres__iconRing" cx="26" cy="26" r="24" fill="none" strokeWidth="3" />
        <path className="payres__iconDraw" fill="none" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round" d="M26 15v12l8 5" />
    </svg>
);

const LockIcon = () => (
    <svg viewBox="0 0 52 52" aria-hidden="true">
        <rect className="payres__iconRing" x="13" y="23" width="26" height="20" rx="4" fill="none" strokeWidth="3" />
        <path className="payres__iconDraw" fill="none" strokeWidth="3" strokeLinecap="round" d="M19 23v-5a7 7 0 0 1 14 0v5" />
        <circle className="payres__iconDot" cx="26" cy="33" r="2.6" stroke="none" />
    </svg>
);

// onResumeDraft (optional): the client left a HALF-FILLED booking request to go
// top up their wallet by card. On success the money is in the wallet but the
// booking still isn't requested — so the paid state offers to walk straight
// back into the saved draft instead of dumping them on a list page to start
// over. Only the host knows a draft exists, so it passes the callback.
export default function PaymentResultOverlay({ payReturn, surface = 'wallet', fallbackAmount = null, onEditResubmit = null, onResumeDraft = null }) {
    const { status, result, dismiss, loginUrl, orderRef, setUnauthenticated } = payReturn || {};

    // Retry / cancel rails for the failed & expired states.
    // action: null | 'retrying' | 'cancelling' | 'cancelled'
    //       | 'paid_after_cancel' (the cancel attempt discovered the payment had
    //         actually COMPLETED — never claim "nothing was charged")
    //       | 'in_review' (409 PAYMENT_IN_REVIEW — the gateway is still holding
    //         the money; calm persistent info, NOT an error)
    const [action, setAction] = useState(null);
    const [actionError, setActionError] = useState('');
    const [actionInfo, setActionInfo] = useState('');
    // The fresh figure the server quoted on PENDING_DRIFT. Sent back as
    // expected_total on the next retry so the server accepts the amount the
    // client has now been shown and acknowledged — without it, every retry
    // re-compares against the stale baseline and dead-ends forever.
    const [ackTotal, setAckTotal] = useState(null);

    // RETRY, ON THIS SCREEN (2026-08-16). The old flow restaged the intent and
    // sent the client to the paypage; now the card is re-entered right here and
    // the restaged order is completed against that session. Everything the
    // retry endpoint does is unchanged — same intent, same guards, same drift
    // check — only the gateway leg differs. 'off' = the SDK could not mount, and
    // the redirect underneath is used exactly as before.
    const [retryHostedRail, setRetryHostedRail] = useState('probe');
    const [retryEntering, setRetryEntering] = useState(false);


    const open = Boolean(status);
    const busy = action === 'retrying' || action === 'cancelling';
    const dismissible = open && status !== 'checking' && !busy;

    // dismiss is a fresh closure every render — keep the latest for listeners.
    const dismissRef = useRef(dismiss);
    dismissRef.current = dismiss;

    // A new verification pass resets any stale retry/cancel state.
    useEffect(() => {
        setAction(null);
        setActionError('');
        setActionInfo('');
        setAckTotal(null);
    }, [status]);

    // Escape closes any dismissible state.
    useEffect(() => {
        if (!dismissible) return undefined;
        const onKey = (e) => { if (e.key === 'Escape' && dismissRef.current) dismissRef.current(); };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [dismissible]);

    // Lock page scroll behind the overlay.
    useEffect(() => {
        if (!open) return undefined;
        const prev = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        return () => { document.body.style.overflow = prev; };
    }, [open]);

    if (!open) return null;

    const s = SURFACE[surface] || SURFACE.wallet;
    const amount = result?.amount != null ? result.amount : fallbackAmount;
    const purpose = result?.kind_label || result?.purpose || s.purposeFallback;
    const editKind = isEditKind(result);
    const autopaySetupKind = isAutopaySetupKind(result);

    // The panel hands us a session; the server restages and completes.
    const startRetryPayment = async (sessionId) => {
        setActionError('');
        try {
            const body = { order_ref: orderRef, session_id: sessionId };
            if (ackTotal != null) body.expected_total = ackTotal;
            const res = await axios.post('/api/payments/retry', body);
            if (!res.data?.payment_response) {
                setActionError('Could not restart the payment. Please try again.');
                return null;
            }
            return res.data;
        } catch (err) {
            if (err.response?.status === 401 && setUnauthenticated) {
                setUnauthenticated();
                return null;
            }
            if (isPendingDrift(err)) {
                setAckTotal(driftCaptureTotal(err));
                setActionInfo(pendingDriftMessage(err));
                setRetryEntering(false);
                return null;
            }
            if (isPaymentInReview(err)) {
                setActionInfo(paymentInReviewMessage(err));
                setAction('in_review');
                setRetryEntering(false);
                return null;
            }
            setActionError(paymentErrorMessage(err, 'Could not restart the payment. Please try again.'));
            return null;
        }
    };

    const handleRetry = async () => {
        if (!orderRef || busy) return;
        setAction('retrying');
        setActionError('');
        setActionInfo('');
        try {
            // ackTotal is set only after the server told us the amount moved and
            // we SHOWED that figure to the client — sending it back is their
            // acknowledgement, and it is what unsticks the retry rail.
            const body = { order_ref: orderRef };
            if (ackTotal != null) body.expected_total = ackTotal;
            const res = await axios.post('/api/payments/retry', body);
            if (res.data?.payment_url) {
                window.location.assign(res.data.payment_url);
                return; // navigating away — keep the busy state on
            }
            setAction(null);
            setActionError('Could not restart the payment. Please try again.');
        } catch (err) {
            // Session gone (logout elsewhere / rotated secret): show the calm
            // login card that round-trips back here, NOT a hard logout that
            // would destroy this outcome screen and the order ref.
            if (err.response?.status === 401 && setUnauthenticated) {
                setAction(null);
                setUnauthenticated();
                return;
            }
            // The amount due moved. Quote the fresh figure and remember it —
            // the next tap sends it as expected_total and goes through.
            if (isPendingDrift(err)) {
                setAction(null);
                setAckTotal(driftCaptureTotal(err));
                setActionInfo(pendingDriftMessage(err));
                return;
            }
            // The gateway still holds this payment — calm, persistent, not red.
            if (isPaymentInReview(err)) {
                setActionInfo(paymentInReviewMessage(err));
                setAction('in_review');
                return;
            }
            setAction(null);
            setActionError(paymentErrorMessage(err, 'Could not restart the payment. Please try again.'));
        }
    };

    const handleCancel = async () => {
        if (!orderRef || busy) return;
        setAction('cancelling');
        setActionError('');
        setActionInfo('');
        try {
            const res = await axios.post('/api/payments/cancel', { order_ref: orderRef });
            // /cancel answers 200 with the checkout's RESULTING outcome. It can
            // legitimately be 'paid': the client finished on the paypage but the
            // redirect was lost, so the cancel attempt found money in flight,
            // ran the idempotent confirm and the capture settled. Claiming
            // "nothing was charged" there is a falsehood about money — it invites
            // a duplicate booking and a bank dispute, right as the truthful
            // "payment received" WhatsApp lands.
            //
            // ONE reader decides (shared with mobile). Fail CLOSED: only an
            // EXPLICIT not-paid outcome may claim nothing was charged; missing /
            // renamed / unknown / 'pending' → 'unconfirmed'.
            const settlement = readCancelSettlement(res);
            if (settlement === 'paid') {
                setAction('paid_after_cancel');
                return;
            }
            if (settlement === 'cancelled') {
                setAction('cancelled');
                return;
            }
            setActionInfo(CANCEL_UNCONFIRMED_MESSAGE);
            setAction('unconfirmed');
        } catch (err) {
            if (err.response?.status === 401 && setUnauthenticated) {
                setAction(null);
                setUnauthenticated();
                return;
            }
            // Same single reader for the thrown leg. 'review' = money still in
            // flight at the gateway and the void could not be verified — calm,
            // informational, never a red failure. 'cancelled' is reached ONLY
            // when the body says so, so a bare 409/503 from a proxy or a future
            // backend code lands in 'unconfirmed' and never prints "nothing was
            // charged".
            const settlement = readCancelErrorSettlement(err);
            if (settlement === 'review') {
                setActionInfo(paymentInReviewMessage(err));
                setAction('in_review');
                return;
            }
            if (settlement === 'paid') {
                setAction('paid_after_cancel');
                return;
            }
            if (settlement === 'cancelled') {
                setAction('cancelled');
                return;
            }
            // 'unconfirmed'. A transport-level failure (no response at all)
            // still has to surface as a retryable error rather than a permanent
            // screen — but anything the SERVER answered about this checkout
            // keeps the persistent "still confirming" state.
            if (err.response) {
                setActionInfo(CANCEL_UNCONFIRMED_MESSAGE);
                setAction('unconfirmed');
            } else {
                setAction(null);
                setActionError(paymentErrorMessage(err, 'Could not cancel the payment. Please try again.'));
            }
        }
    };

    // Re-verify from the 'unconfirmed' state. /payments/verify is idempotent and
    // read-only, so this is always safe to press. It can only ever move the
    // client to a state we have positively verified — it never invents one.
    const handleReverify = async () => {
        if (!orderRef || action === 'reverifying') return;
        const previous = action;
        setAction('reverifying');
        try {
            const res = await axios.post('/api/payments/verify', { order_ref: orderRef });
            // Same single reader as /cancel — /verify answers the identical
            // outcome contract, so it must not re-derive the decision.
            const settlement = readCancelSettlement(res);
            if (settlement === 'paid') { setAction('paid_after_cancel'); return; }
            if (settlement === 'cancelled') { setAction('cancelled'); return; }
            setActionInfo(CANCEL_UNCONFIRMED_MESSAGE);
            setAction(previous);
        } catch (err) {
            if (err.response?.status === 401 && setUnauthenticated) {
                setAction(null);
                setUnauthenticated();
                return;
            }
            setActionInfo(CANCEL_UNCONFIRMED_MESSAGE);
            setAction(previous);
        }
    };

    let tone; let icon; let title; let body; let actions;

    if (action === 'paid_after_cancel') {
        // Truthful correction: the cancel found a COMPLETED outcome. Reuse the
        // paid language exactly — the intent went ahead. On the account (card
        // setup) surface "completed" means the card was verified and saved —
        // never framed as a payment.
        tone = 'paid';
        icon = <CheckIcon />;
        title = surface === 'account' ? 'Your card was already saved' : 'Your payment actually completed';
        body = surface === 'account'
            ? `This card setup could not be cancelled because it had already completed. ${s.paidBody}`
            : `This payment could not be cancelled because it had already gone through. ${s.paidBody}`;
        actions = (
            <button type="button" className="payres__btn payres__btn--primary" onClick={dismiss}>
                {s.cta}
            </button>
        );
    } else if (action === 'unconfirmed' || action === 'reverifying') {
        // The cancel call came back in a shape we could not read as a definite
        // outcome. We do NOT know whether money moved, so we say exactly that
        // and steer the client away from paying twice. Amber, never red, never
        // "nothing was charged".
        tone = 'amber';
        icon = <ClockIcon />;
        title = 'We’re still confirming this payment';
        body = actionInfo || CANCEL_UNCONFIRMED_MESSAGE;
        actions = (
            <>
                <button
                    type="button"
                    className="payres__btn payres__btn--primary"
                    onClick={handleReverify}
                    disabled={action === 'reverifying'}
                >
                    {action === 'reverifying' ? 'Checking…' : 'Check again'}
                </button>
                <button type="button" className="payres__btn" onClick={dismiss}>
                    {s.cta}
                </button>
            </>
        );
    } else if (action === 'in_review') {
        // Money-in-flight: calm and persistent. No red, no "try again" —
        // there is nothing for the client to do but wait for the bank.
        tone = 'amber';
        icon = <ClockIcon />;
        title = 'Your payment is being reviewed';
        body = actionInfo;
        actions = (
            <button type="button" className="payres__btn payres__btn--primary" onClick={dismiss}>
                {s.cta}
            </button>
        );
    } else if (action === 'cancelled') {
        // Persistent post-cancel confirmation — replaces the failed/expired card.
        tone = 'neutral';
        icon = <CrossIcon />;
        title = s.cancelledTitle || 'Payment cancelled';
        body = surface === 'bookings' && autopaySetupKind
            ? 'The card verification has been cancelled — your card was not saved, nothing was charged and the booking request was not submitted.'
            : s.cancelledBody;
        actions = (
            <>
                {/* The host passes onEditResubmit ONLY when a saved booking
                    draft actually exists, so the button is offered wherever it
                    was passed — a cancelled mid-booking top-up returns to the
                    WALLET surface (item 4), and the draft is just as stranded
                    there as on bookings. No surface gate. */}
                {onEditResubmit && (
                    <button type="button" className="payres__btn payres__btn--primary" onClick={onEditResubmit}>
                        Edit &amp; resubmit request
                    </button>
                )}
                <button
                    type="button"
                    className={`payres__btn ${onEditResubmit ? 'payres__btn--secondary' : 'payres__btn--primary'}`}
                    onClick={dismiss}
                >
                    Done
                </button>
            </>
        );
    } else {
        switch (status) {
            case 'checking':
                tone = 'checking';
                icon = <div className="payres__spinner" aria-hidden="true" />;
                title = s.checkingTitle || 'Confirming your payment…';
                body = 'We’re checking with the bank — this usually takes a few seconds. Please keep this page open.';
                actions = null;
                break;
            case 'paid':
                tone = 'paid';
                icon = <CheckIcon />;
                title = surface === 'bookings' && autopaySetupKind && !onResumeDraft
                    ? 'Card saved — booking requested'
                    : s.paidTitle || 'Payment successful';
                body = onResumeDraft
                    ? 'Your wallet has been topped up. Your booking request is still waiting to be sent — pick up where you left off, nothing you filled in was lost.'
                    : surface === 'bookings' && autopaySetupKind
                        // NOTHING was captured — no booking money, no wallet
                        // credit. Say what WILL be charged and when, and that
                        // the verification hold is released.
                        ? 'Card saved. Your booking is requested — the booking amount will be charged to this card only after the cleaning is completed. The temporary AED 1.00 hold is released automatically — you will not be charged for it.'
                        : surface === 'bookings' && isTopupKind(result)
                            // A top-up / settlement returning to the bookings
                            // surface files NO booking request — the generic
                            // bookings paidBody would falsely claim one was
                            // submitted. Say only what verifiably happened.
                            ? (isAdjustmentFunding(result)
                                ? 'Your payment was received — it is being applied to your price adjustments now.'
                                : 'Your wallet has been topped up.')
                            : surface === 'bookings' && editKind
                                ? 'Your payment was received and your booking change request has been submitted for review. We’ll notify you the moment our team confirms it.'
                                : s.paidBody;
                actions = onResumeDraft ? (
                    // The money landed but the BOOKING was never submitted. The
                    // primary action has to be the unfinished thing, or the
                    // client reads "Payment successful" as "booking done".
                    <>
                        <button type="button" className="payres__btn payres__btn--primary" onClick={onResumeDraft}>
                            Continue my booking request
                        </button>
                        <button type="button" className="payres__btn payres__btn--secondary" onClick={dismiss}>
                            Not now
                        </button>
                    </>
                ) : (
                    <button type="button" className="payres__btn payres__btn--primary" onClick={dismiss}>
                        {surface === 'bookings' && editKind
                            ? 'View my bookings'
                            // No booking request exists for a top-up kind, so
                            // "View my booking request" would point at nothing.
                            : surface === 'bookings' && isTopupKind(result)
                                ? 'Back to bookings'
                                : s.cta}
                    </button>
                );
                break;
            case 'verified':
                // CARD VERIFICATION success (AUTH + verified void rail): the
                // card is saved; NOTHING was captured and no wallet row
                // exists. Never framed as a payment — no amount, no purpose
                // line (the figure-suppression below keys off status==='paid',
                // so nothing extra to do). The body is the backend's own
                // truthful hold line (verification.message).
                tone = 'paid';
                icon = <CheckIcon />;
                title = surface === 'bookings' && autopaySetupKind
                    ? 'Card saved — booking requested'
                    : (s.verifiedTitle || 'Card verified and saved');
                body = verifiedStateBody(result, surface);
                actions = (
                    <button type="button" className="payres__btn payres__btn--primary" onClick={dismiss}>
                        {s.cta}
                    </button>
                );
                break;
            case 'failed':
            case 'expired': {
                const isExpired = status === 'expired';
                tone = isExpired ? 'amber' : 'failed';
                icon = isExpired ? <ClockIcon /> : <CrossIcon />;
                title = isExpired
                    ? (s.expiredTitle || 'Payment session expired')
                    : (s.failedTitle || 'Payment was not completed');
                body = surface === 'bookings' && autopaySetupKind
                    // withdrawLinkedRequest unwinds the staged auto-charge
                    // request on every terminal non-paid state, so nothing is
                    // requested and no card was stored.
                    ? (isExpired
                        ? 'The secure page timed out before your card was verified. Your card was not saved, your booking request has not been submitted, and no money was taken. Your details are saved, so trying again needs no re-typing.'
                        : 'Your card was not saved and your booking request has not been submitted. No money was taken. Your details are saved — trying again picks up right where you left off.')
                    : isExpired ? s.expiredBody : s.failedBody;
                actions = (
                    <>
                        {retryEntering && retryHostedRail !== 'off' ? (
                            /* The card is re-entered HERE — the failure and the
                               form the client needs are one screen, not a
                               redirect apart. */
                            <div className="payres__embed">
                                <HostedCardPanel
                                    payLabel={amount != null ? `Pay ${money(amount)}` : 'Pay now'}
                                    amountAed={Number(amount) || 0}
                                    startPayment={startRetryPayment}
                                    onUnavailable={() => { setRetryHostedRail('off'); setRetryEntering(false); }}
                                    onPaid={(res) => {
                                        // Paid. Re-enter the standard return flow
                                        // keyed to the NEW order, so the outcome
                                        // screen is the same server-verified one
                                        // every other payment lands on — no second
                                        // success screen to keep in step.
                                        const ref = res?.order_ref;
                                        if (ref) {
                                            window.location.assign(
                                                `${window.location.pathname}?payment=return&ref=${encodeURIComponent(ref)}`
                                            );
                                        } else {
                                            setRetryEntering(false);
                                        }
                                    }}
                                    onFailed={() => { /* the panel states its own outcome and re-arms */ }}
                                />
                            </div>
                        ) : (
                            <button
                                type="button"
                                className="payres__btn payres__btn--primary"
                                onClick={retryHostedRail === 'off' ? handleRetry : () => setRetryEntering(true)}
                                disabled={busy}
                            >
                                {action === 'retrying' ? 'Opening secure page…' : (s.retryLabel || 'Try payment again')}
                            </button>
                        )}
                        <button
                            type="button"
                            className="payres__btn payres__btn--ghostDanger"
                            onClick={handleCancel}
                            disabled={busy}
                        >
                            {action === 'cancelling' ? 'Cancelling…' : (s.cancelLabel || 'Cancel this payment')}
                        </button>
                    </>
                );
                break;
            }
            case 'still_pending':
                tone = 'amber';
                icon = <ClockIcon />;
                title = s.pendingTitle || 'Still confirming your payment';
                body = s.pendingBody;
                actions = (
                    <button type="button" className="payres__btn payres__btn--secondary" onClick={dismiss}>
                        Close
                    </button>
                );
                break;
            case 'not_found':
                tone = 'neutral';
                icon = <LockIcon />;
                title = 'Payment not found on this account';
                body = 'We couldn’t match this payment to the account you are logged in with. If you completed a payment, it may belong to a different login — nothing further will update here.';
                actions = (
                    <button type="button" className="payres__btn payres__btn--primary" onClick={dismiss}>
                        Close
                    </button>
                );
                break;
            case 'unauthenticated':
                tone = 'neutral';
                icon = <LockIcon />;
                title = 'Log in to continue';
                // Outcome-neutral by design: the return leg fires for cancelled and
                // failed payments too, and nothing has been verified yet — never
                // claim success the gateway hasn't confirmed.
                body = 'Your secure payment session has ended. Log in to see the verified result of this payment on your account.';
                actions = (
                    <button
                        type="button"
                        className="payres__btn payres__btn--primary"
                        onClick={() => window.location.assign(loginUrl || '/login')}
                    >
                        Log in
                    </button>
                );
                break;
            default:
                return null;
        }
    }

    return (
        <div
            className="payres"
            role="dialog"
            aria-modal="true"
            aria-labelledby="payres-title"
            onMouseDown={(e) => {
                if (dismissible && e.target === e.currentTarget && dismiss) dismiss();
            }}
        >
            <div className={`payres__card payres__card--${tone}`} aria-live="polite">
                {dismissible && (
                    <button type="button" className="payres__close" onClick={dismiss} aria-label="Close">
                        &times;
                    </button>
                )}
                <div className={`payres__icon payres__icon--${tone}`}>{icon}</div>
                <h2 id="payres-title" className="payres__title">{title}</h2>
                {/* CARD SETUP shows NO payment figures (P1): the outcome is
                    "Card saved", not a payment result — an "AED 1.00" line here
                    would read as a receipt for a charge that never happened.
                    Applies to the account surface and the auto-pay first-card
                    rail returning to bookings. */}
                {status === 'paid' && action !== 'cancelled' && amount != null
                    && surface !== 'account' && !autopaySetupKind && (
                    <div className="payres__amount">{money(amount)}</div>
                )}
                {status === 'paid' && action !== 'cancelled'
                    && surface !== 'account' && !autopaySetupKind
                    && <div className="payres__purpose">{purpose}</div>}
                <p className="payres__body">{body}</p>
                {/* Calm informational note (amount changed / payment in review)
                    — deliberately NOT the red actionError styling. */}
                {actionInfo && action !== 'in_review' && (
                    <p className="payres__actionInfo">{actionInfo}</p>
                )}
                {actionError && <p className="payres__actionError">{actionError}</p>}
                {actions && <div className="payres__actions">{actions}</div>}
            </div>
        </div>
    );
}
